import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Spinner } from "../../../shared";
import { useConfig, useForm } from "../../../hooks";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/access-control";
import { LEG_FORM_CONFIG } from "../../../constants/validations/leg";
import { getProductTerminology } from "../../../constants/product-terminology";
import { useUpdateJobMutation } from "../../../redux/services/jobs/jobsApi";
import { useGetJobV2Query } from "../../../redux/services/jobs/jobsV2Api";
import DeleteJobModal from "../DeleteModal";
import { LegCreateModal } from "../Legs/LegCreate/Modal";
import { DispatchDetails } from "./DispatchDetails";
import { FinancialSummary } from "./FinancialSummary";
import { JobWorkspaceFrame } from "./Frame";
import { JobEditPanel } from "./JobEditPanel";
import { JobOverview } from "./JobOverview";
import { LegPanel } from "./LegPanel";
import { LoadsAndLegs } from "./LoadsAndLegs";
import { downloadWorkspaceExport } from "./jobExport";
import { buildJobWorkspace } from "./jobWorkspaceModel";
import {
  createLegEditForm,
  createLegFormDefaults,
  createReturnLegForm,
} from "./legEditor";

/**
 * Jobs v2 detail page.
 * Routed only when `jobs.v2.enabled` is explicitly on.
 * Edit, add leg, and leg rows stay on this page. Service and repair tenants
 * still open the classic job for leg entry.
 * @returns {JSX.Element}
 */
export function JobWorkspacePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { configs, appType } = useConfig();
  const terms = getProductTerminology(appType);
  const { hasAnyPermission, hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.JOBS_EDIT);
  const canDelete = hasPermission(PERMISSIONS.JOBS_DELETE);
  const canAddLeg = hasAnyPermission([
    PERMISSIONS.JOBS_CREATE,
    PERMISSIONS.JOBS_EDIT,
    PERMISSIONS.JOBS_ASSIGN,
  ]);
  const legModalEnabled = appType !== "serviceRepair";
  const legFormApi = useForm(legModalEnabled ? LEG_FORM_CONFIG : {});
  const [updateJob, { isLoading: savingJob }] = useUpdateJobMutation();
  const { data: job, isLoading, isError } = useGetJobV2Query(
    { jobId: id },
    { skip: !id }
  );
  const [editOpen, setEditOpen] = useState(false);
  const [legModalOpen, setLegModalOpen] = useState(false);
  const [legModalMounted, setLegModalMounted] = useState(false);
  const [selectedLegId, setSelectedLegId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const view = useMemo(
    () =>
      buildJobWorkspace(job, {
        companyLabel:
          configs?.parentCompany?.slug || configs?.parentCompany?.name || "",
        legsTitle: appType === "serviceRepair" ? terms.legPlural : "Loads & legs",
      }),
    [appType, configs?.parentCompany?.name, configs?.parentCompany?.slug, job, terms.legPlural]
  );

  useEffect(() => {
    setEditOpen(false);
    setLegModalOpen(false);
    setLegModalMounted(false);
    setSelectedLegId(null);
    setDeleteOpen(false);
  }, [id]);

  const selectedViewLeg = view.legs.find((leg) => leg.id === selectedLegId) || null;
  const selectedLeg =
    (Array.isArray(job?.legs) ? job.legs : []).find(
      (leg) => String(leg?._id || "") === String(selectedLegId || "")
    ) || null;

  const crumbs = [
    { label: "Operations", to: "/" },
    { label: terms.jobPlural, to: "/jobs" },
    { label: job ? view.jobNumber : terms.job },
  ];

  const openClassic = () => navigate(`/jobs/${id}`);

  /**
   * Open the shared leg modal, or the classic job when this tenant does not use it.
   * @param {object} nextForm
   */
  function openLegForm(nextForm) {
    if (!legModalEnabled) {
      openClassic();
      return;
    }
    setSelectedLegId(null);
    setEditOpen(false);
    legFormApi.setFormInit(nextForm, { reset: true });
    setLegModalMounted(true);
    setLegModalOpen(true);
  }

  function openEdit() {
    setSelectedLegId(null);
    setEditOpen(true);
  }

  async function copyJobNumber() {
    try {
      await navigator.clipboard.writeText(view.jobNumber);
      toast.success("Job number copied");
    } catch {
      toast.error("Could not copy the job number");
    }
  }

  function exportJob() {
    downloadWorkspaceExport(view);
    toast.success("Job export downloaded");
  }

  /**
   * @param {object} payload
   */
  async function saveJobEdits(payload) {
    if (!job?._id) return;
    try {
      await updateJob({ id: job._id, data: payload }).unwrap();
      toast.success("Job updated");
      setEditOpen(false);
    } catch (error) {
      toast.error(error?.data?.message || "Could not update this job");
    }
  }

  const moreActions = [
    { title: "Copy job number", onClick: copyJobNumber },
    { title: "Print", onClick: () => window.print() },
    { title: "Open classic job", onClick: openClassic },
  ];
  if (canAddLeg && Array.isArray(job?.legs) && job.legs.length > 0) {
    moreActions.splice(2, 0, {
      title: "Add return leg",
      onClick: () => openLegForm(createReturnLegForm(job)),
    });
  }
  if (canDelete) {
    moreActions.push({ title: "Delete job", onClick: () => setDeleteOpen(true) });
  }

  return (
    <JobWorkspaceFrame crumbs={crumbs}>
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : null}
      {!isLoading && (isError || !job) ? (
        <p className="text-[13px] leading-normal text-[#6e7b84]">
          This job could not be loaded.
        </p>
      ) : null}
      {!isLoading && job ? (
        <>
          <JobOverview
            view={view}
            canEdit={canEdit}
            onEdit={openEdit}
            onExport={exportJob}
            moreActions={moreActions}
          />
          <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_328px]">
            <div className="flex min-w-0 flex-col gap-6">
              <LoadsAndLegs
                view={view}
                canAddLeg={canAddLeg}
                onAddLeg={() => openLegForm(createLegFormDefaults(job))}
                onSelectLeg={(leg) => setSelectedLegId(leg.id)}
                selectedLegId={selectedLegId}
              />
              <DispatchDetails view={view} onEditNote={canEdit ? openEdit : undefined} />
            </div>
            <FinancialSummary view={view} />
          </div>
          <JobEditPanel
            open={editOpen}
            job={job}
            saving={savingJob}
            onClose={() => setEditOpen(false)}
            onSave={saveJobEdits}
          />
          <LegPanel
            open={Boolean(selectedViewLeg)}
            leg={selectedViewLeg}
            canEdit={canEdit && Boolean(selectedLeg)}
            onClose={() => setSelectedLegId(null)}
            onEdit={() => {
              if (!selectedLeg) return;
              openLegForm(createLegEditForm(selectedLeg, job));
            }}
          />
          {legModalEnabled && legModalMounted ? (
            <LegCreateModal
              visible={legModalOpen}
              setVisible={setLegModalOpen}
              onCancel={() => setLegModalOpen(false)}
              form={legFormApi}
              job={job}
            />
          ) : null}
          <DeleteJobModal
            visible={deleteOpen}
            setVisible={setDeleteOpen}
            jobs={[job]}
            deselectItems={() => {}}
          />
        </>
      ) : null}
    </JobWorkspaceFrame>
  );
}

export default JobWorkspacePage;
